import React, { useRef } from 'react';
import { Modal, Button, Typography, Tag, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, CheckCircleOutlined, ClockCircleOutlined, CalendarOutlined, UserOutlined, BookOutlined } from '@ant-design/icons';
import moment from 'moment';
import hash from 'hash-sum';
import { QRCodeSVG } from 'qrcode.react';


const { Text } = Typography;

const FeedbackPrintModal = ({ visible, onClose, feedback, classInfo, teacherInfo, studentInfo }) => {
  const { t } = useTranslation();
  const printRef = useRef();

  const formatTime = (timeStr, plusMinutes = 0) => {
    if (!timeStr) return '-';
    const time = moment(`2000-01-01T${timeStr}`).add(plusMinutes, 'minutes');
    return time.format('HH:mm');
  };

  const formatDate = (dateStr) => {
    return moment(dateStr).format('DD MMM YY').toUpperCase();
  };

  // Format field names to display names
  const formatFieldName = (field) => {
    const fieldNames = {
      warmup: 'Warm Up',
      task_1: 'TASK 1',
      task_2: 'TASK 2', 
      task_3: 'TASK 3',
      homework: 'Homework',
      reflection: 'Reflection',
      comment: 'Additional Comments'
    };
    return fieldNames[field] || field;
  };

  // Get field icon
  const getFieldIcon = (field) => {
    const icons = {
      warmup: '🏃‍♂️',
      task_1: '📝',
      task_2: '📋',
      task_3: '✅',
      homework: '📚',
      reflection: '💭',
      comment: '💬'
    };
    return icons[field] || '📄';
  };

  const generateFeedbackUID = (feedback) => {
  return hash({
    id: feedback.id,                        // primary key
    classid: feedback.classid,
    student_id: feedback.student_id,
    teacher_id: feedback.teacher_id,
    created_at: feedback.created_at,
  });
};

  const handleDownloadPDF = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '', 'width=800,height=1000');
    // Set the document title first
    const nickname = studentInfo?.nickname || 'Student';
    const classid = feedback?.classid || 'Class';
    const date = moment(feedback?.date).format('DDMMMYYYY');
    const documentTitle = `${nickname}_${classid}_Student_Report_${date}`;
    printWindow.document.write(`
      <html>
        <head>
          <title>${documentTitle}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body { 
              font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif; 
              padding: 30px; 
              background: #ffffff;
              color: #333;
              line-height: 1.6;
            }
            
            .print-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
            }
            
            .header {
              background: white;
              border: 2px solid #29166f;
              color: #333;
              padding: 25px 30px;
              border-radius: 12px 12px 0 0;
              margin-bottom: 0;
            }
            
            .header-content {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .logo-section {
              display: flex;
              align-items: center;
              gap: 20px;
            }
            
            .logo {
              height: 120px;
              display: flex;
              align-items: center;
            }
            
            .header-title {
              font-size: 24px;
              font-weight: 600;
              margin: 0;
              color: #29166f;
            }
            
            .teacher-info {
              text-align: right;
              font-size: 14px;
              color: #29166f;
            }
            
            .info-section-feedback {
              background: #f8fafe;
              border: 1px solid #e6f0ff;
              border-radius: 0 0 12px 12px;
              padding: 25px 30px;
              margin-top: 10px;
              margin-bottom: 10px;
            }
          
            
            .info-item {
              display: flex;
              align-items: center;
              gap: 5px;
              padding: 10px 0;
            }
            
            .info-icon {
              font-size: 16px;
              color: #29166f;
              width: 20px;
            }
            
            .info-label {
              font-weight: 600;
              color: #29166f;
              min-width: 80px;
            }
            
            .info-value {
              color: #333;
              flex: 1;
            }
            
            .status-tag {
              display: inline-flex;
              align-items: center;
              gap: 5px;
              padding: 4px 12px;
              border-radius: 20px;
              font-weight: 500;
              font-size: 12px;
            }
            
            .status-approved {
              background-color: #f6ffed;
              border: 1px solid #b7eb8f;
              color: #389e0d;
            }
            
            .status-pending {
              background-color: #fff7e6;
              border: 1px solid #ffd591;
              color: #d46b08;
            }
            
            .content-section {
              margin-bottom: 25px;
            }
            
            .section-title {
              background: linear-gradient(135deg, #29166f 0%, #4a2c96 100%);
              color: white;
              padding: 15px 20px;
              border-radius: 8px 8px 0 0;
              font-size: 18px;
              font-weight: 600;
              margin: 0;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            
            .feedback-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 20px;
              background: white;
              border: 1px solid #e6f0ff;
              border-top: none;
              border-radius: 0 0 8px 8px;
              padding: 25px;
            }
            
            .feedback-item {
              background: #fafbfc;
              border: 1px solid #e1e8ed;
              border-radius: 8px;
              padding: 20px;
              transition: all 0.2s ease;
            }
            
            .feedback-label {
              display: flex;
              align-items: center;
              gap: 8px;
              font-weight: 600;
              color: #29166f;
              margin-bottom: 10px;
              font-size: 14px;
            }
            
            .feedback-content {
              background: white;
              border: 1px solid #e1e8ed;
              border-radius: 6px;
              padding: 15px;
              color: #333;
              font-size: 14px;
              line-height: 1.6;
              min-height: 60px;
            }
            
            .empty-content {
              color: #999;
              font-style: italic;
            }
            
            .footer-feedback {
              text-align: center;
              padding: 20px;
              border-top: 2px solid #29166f;
              margin-top: 30px;
              color: #666;
              font-size: 12px;
            }
            
            .footer-feedback-line {
              margin: 5px 0;
            }
            
            /* Print-specific styles */
            @media print {
              body {
                margin: 0;
                padding: 15px;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .print-container {
                max-width: none;
                margin: 0;
              }
              
              .header,
              .section-title,
              .status-tag,
              .feedback-item {
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
            }
            
            @page {
              margin: 1cm;
              size: A4;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printContent}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
      printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
  };

  if (!feedback) return null;

  const isApproved = feedback.approve === 'approved';

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose} size="large">
          {t('Close')}
        </Button>,
        <Button 
          key="download" 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={handleDownloadPDF}
          size="large"
        >
          {t('Download PDF')}
        </Button>,
      ]}
      style={{ top: 20 }}
    >
      <div ref={printRef}>
        {/* Modern Header */}
        <div 
          style={{
            background: 'white',
            border: '2px solid #29166f',
            color: '#333',
            padding: '25px 30px',
            borderRadius: '12px 12px 0 0',
            marginBottom: 0
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px'
            }}>
              <div style={{ height: '120px', display: 'flex', alignItems: 'center' }}>
                <img 
                  src="/images/tfulogo.png" 
                  alt="TFU Logo" 
                  style={{ height: '120px', width: 'auto' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: '600',
                  margin: 0,
                  color: '#29166f'
                }}>
                  Student Feedback Report
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                  Academic Performance Evaluation
                </div>
              </div>
            </div>
            <div style={{
              textAlign: 'right',
              fontSize: '14px',
              color: '#29166f'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>STUDENT</div>
              <div>{studentInfo?.firstname || ''} {studentInfo?.lastname || ''} ({studentInfo?.nickname || '-'})</div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section-feedback">
            <div className="info-item">
              <BookOutlined className="info-icon" />
              <span className="info-label">Class:</span>
              <span className="info-value">{classInfo?.class_code || '-'}</span>
            </div>
            <div className="info-item">
              <BookOutlined className="info-icon" />
              <span className="info-label">Subject:</span>
              <span className="info-value">{classInfo?.subject_name || '-'}</span>
            </div>
            <div className="info-item">
              <CalendarOutlined className="info-icon" />
              <span className="info-label">Date:</span>
              <span className="info-value">
                {formatDate(feedback.date)} at {formatTime(feedback.schedule_time)} - {formatTime(feedback.schedule_time, feedback.schedule_hour)}
              </span>
            </div>
            <div className="info-item">
              <UserOutlined className="info-icon" />
              <span className="info-label">Teacher:</span>
              <span className="info-value">
                {teacherInfo?.nickname || ''} 
              </span>
          </div>
          
          <Divider style={{ margin: '15px 0' }} />
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text strong style={{ color: '#29166f' }}>Status: </Text>
              <Tag 
                icon={isApproved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                color={isApproved ? 'success' : 'warning'}
                style={{ fontWeight: '500' }}
              >
                {isApproved ? t('Approved') : t('Pending')}
              </Tag>
            </div>

          </div>
        </div>

        {/* Content Section */}
        <div className="content-section">
          <div className="section-title">
            📚 Learning Activities & Assessment
          </div>
          <div className="feedback-grid">
            {['warmup', 'task_1', 'task_2', 'task_3', 'homework', 'reflection', 'comment'].map((field) => (
              <div key={field}>
                <div className="feedback-label">
                  <span>{getFieldIcon(field)}</span>
                  <span>{formatFieldName(field)}</span>
                </div>
                <div className="feedback-content">
                  {feedback[field] ? (
                    <div>{feedback[field]}</div>
                  ) : (
                    <div className="empty-content">No feedback provided</div>
                  )}
                </div>
                
              </div>
            ))}
                      <div className="footer-feedback-line" style={{ marginTop: '10px', color: 'red', fontWeight: '200' }}>
            This document is generated automatically and contains confidential student information
          </div>
          </div>
               {/* Footer */}
        <div className="footer-feedback">
          <div className="footer-feedback-line">
            <strong>TFU Language School</strong>
          </div>
          <div className="footer-feedback-line">
            Last updated: {formatDate(feedback.updated_on)} | Report ID: {feedback.id || 'N/A'}
          </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
              Generated on {moment().format('DD MMM YY, HH:mm')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px' }}>
                <QRCodeSVG
                  value={`TFU-${generateFeedbackUID(feedback)}`}
                  size={50}
                  style={{ display: 'block', margin: 'auto' }}
                />
              </div>
           <div style={{ fontSize: '12px', color: '#888' }}>
              Feedback Ref: <span style={{ fontWeight: 600 }}>TFU-{generateFeedbackUID(feedback)}</span>
            </div>
        </div>
        </div>

   
      </div>
    </Modal>
  );
};

export default FeedbackPrintModal;